import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../../components/public/PageHero';
import PublicLayout from '../../components/public/PublicLayout';
import { APPLY_ROUTE, withBase } from '../../publicSite/config';
import { formatEventWindow, splitEventsByTimeline } from '../../publicSite/events';
import { usePageMeta } from '../../publicSite/usePageMeta';
import { usePublicEvents } from '../../publicSite/usePublicEvents';

const eventDateParts = (event) => {
  const parsed = new Date(event?.eventStartDate || event?.eventDate || '');
  if (Number.isNaN(parsed.getTime())) return { day: '—', month: 'TBC' };
  return {
    day: parsed.toLocaleDateString('en-ZA', { day: '2-digit' }),
    month: parsed.toLocaleDateString('en-ZA', { month: 'short' }).toUpperCase()
  };
};

const signupPath = (event) => (
  `/events/signup?eventId=${encodeURIComponent(event?.id || '')}` +
  `&title=${encodeURIComponent(event?.title || 'School of Shem Event')}` +
  `&date=${encodeURIComponent(formatEventWindow(event?.eventStartDate || event?.eventDate, event?.eventEndDate))}` +
  `&image=${encodeURIComponent(event?.downloadUrl || event?.image || '')}`
);

function EventList({ events, emptyTitle }) {
  if (events.length === 0) return <p className="ps-empty-state">{emptyTitle}</p>;

  return (
    <div className="public-event-list">
      {events.map((event) => {
        const date = eventDateParts(event);
        return (
          <article className="public-event-row public-event-row-detailed" key={event.id}>
            <time className="public-event-date" dateTime={event.eventStartDate || event.eventDate || ''}>
              <strong>{date.day}</strong>
              <span>{date.month}</span>
            </time>
            <div>
              <h3>{event.title}</h3>
              <p>{event.description || event.notes || 'School of SHEM event'}</p>
              <small>{formatEventWindow(event.eventStartDate || event.eventDate, event.eventEndDate)}</small>
            </div>
            <span>{event.location || event.eventLocation || event.module || 'Details on registration'}</span>
            <div className="public-event-actions">
              <Link className="public-text-link" to={withBase(signupPath(event))}>
                Register <span aria-hidden="true">→</span>
              </Link>
              {event.eventLink && (
                <a className="public-subtle-link" href={event.eventLink} target="_blank" rel="noreferrer">
                  Event details
                </a>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}

export default function Events() {
  usePageMeta(
    'Events | School of Shem',
    'See upcoming School of Shem events and register through the official event form.'
  );
  const { events, loading } = usePublicEvents();
  const { upcoming, past } = splitEventsByTimeline(events);

  return (
    <PublicLayout>
      <PageHero
        eyebrow="Gather and grow"
        title="Events"
        subtitle="Discover upcoming School of SHEM gatherings, teaching sessions, and community opportunities."
        image="/images/shem-community-event.jpg"
        imageAlt="School of SHEM community members connecting at an evening gathering"
      />

      <section className="ps-section">
        <div className="ps-section-inner">
          <div className="ps-hero-eyebrow">Calendar</div>
          <h2 className="ps-section-title">Upcoming events</h2>
          {loading
            ? <p className="ps-empty-state">Loading events…</p>
            : <EventList events={upcoming} emptyTitle="No upcoming events have been posted yet." />}
        </div>
      </section>

      {!loading && past.length > 0 && (
        <section className="ps-section ps-section-muted">
          <div className="ps-section-inner">
            <div className="ps-hero-eyebrow">Archive</div>
            <h2 className="ps-section-title">Past events</h2>
            <EventList events={[...past].reverse()} emptyTitle="No past events are available." />
          </div>
        </section>
      )}

      <section className="ps-section public-cta">
        <div className="ps-section-inner public-cta-inner">
          <div>
            <h2>Join the School of SHEM community.</h2>
            <p>Apply for an academic pathway or contact us about an upcoming event.</p>
          </div>
          <div className="ps-hero-actions">
            <Link to={withBase(APPLY_ROUTE)} className="btn btn-primary">Apply Now</Link>
            <Link to={withBase('/contact')} className="btn btn-outline">Contact Us</Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
